import { useSession } from '../../auth/useSession';
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from '../../auth/passwordSession';
import { getConfig } from '../../config/runtimeConfig';
import { useSelectedWorkspace, useWorkspaceStore } from '../../stores/workspaceStore';
import { Avatar } from '../ui/Avatar';
import { Cover } from '../ui/Cards';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Controls';
import { SearchField } from '../ui/Field';
import { IconBell, IconChevronsUpDown, IconDatabase, IconList, IconLogout, IconPlus, IconSettings, IconUser, IconUserPlus, IconWand } from '../ui/icons';
import { MenuAnchor, MenuDivider, MenuItem, MenuLabel, MenuSurface, useDismiss } from '../ui/Menu';
import { relativeTime, useMarkRead, useNotifications } from '../api/discussion';
import { routeBase, studyName, studyThumbnail, useOpenStudy } from '../api/collections';
import { imageThumbnail, useSearch, type SearchResults } from '../api/explore';
import { imageTag } from '../../data/imageTypes';
import { UploadPill } from './UploadPill';

/**
 * The 56px top bar. Signed in: logo · divider · Collections / Explore ·
 * global search · workspace switcher · Invite · divider · bell · avatar.
 * Guest: logo · Explore · search (filters Explore) · Sign in · Create free
 * account.
 *
 * `guest` is undefined until the session question is answered; the right
 * hand cluster waits for it so a guest never sees the signed-in controls
 * flash, and a member never sees the sign-in links.
 */
const BASE = import.meta.env.BASE_URL.replace(/\/+$/, '');

// The session query lives with the auth code now (both shells read it); the
// community pages keep importing it from here.
export { useSession };

function NavLink({ to, label, active }: { to: string; label: string; active: boolean }) {
  return (
    <Link
      to={to}
      className="tr-hover-track-ink"
      style={{ padding: '6px 10px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: active ? '#f2f2f0' : 'transparent', color: active ? '#0f0f10' : '#6b6b70', textDecoration: 'none' }}
    >
      {label}
    </Link>
  );
}

interface Hit {
  key: string;
  title: string;
  path: string;
  meta: string;
  cover?: string;
  mono?: boolean;
  num?: string;
  open: () => void;
}

/** The prototype's four result sections, five items each, with the total. An image result opens its study on that image. */
function sections(r: SearchResults, base: string, nav: (to: string) => void, openStudy: (studyId: string, series?: string) => void): Array<{ label: string; count: number; items: Hit[] }> {
  const s = (label: string, count: number, items: Hit[]) => (items.length ? [{ label, count, items }] : []);
  return [
    ...s('COLLECTIONS', r.collections.total, r.collections.items.map((c) => ({ key: c.public_id, title: c.name, path: 'Collection', meta: `${c.studies} studies`, cover: c.cover ? studyThumbnail(c.cover) : '#e3e3e1', open: () => nav(`${base}/collections/${c.public_id}`) }))),
    ...s('GROUPS', r.groups.total, r.groups.items.map((g) => ({ key: g.public_id, title: g.name, path: g.collection_name, meta: `${g.studies} studies`, cover: '#fafaf9', num: String(g.position).padStart(2, '0'), open: () => nav(`${base}/collections/${g.collection_id}#${g.public_id}`) }))),
    ...s('STUDIES', r.studies.total, r.studies.items.map((st) => ({ key: st.public_id, title: studyName(st), path: [st.collection_name, st.group_name].filter(Boolean).join(' · ') || 'Not in a collection', meta: `${st.images} images`, cover: studyThumbnail(st.public_id), mono: true, open: () => openStudy(st.public_id) }))),
    ...s('IMAGES', r.images.total, r.images.items.map((im) => ({ key: im.image_id, title: im.original_filename || im.description || im.image_id.slice(-8), path: [im.collection_name, im.study_id].filter(Boolean).join(' · '), meta: imageTag(im.kind, im.image_type), cover: imageThumbnail(im.image_id), mono: true, open: () => openStudy(im.study, im.series_instance_uid) }))),
  ];
}

/** The results menu under the search field (signed in only). */
/** The typed value, settled: one request per pause, not one per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function SearchMenu({ q: typed, wsName, onClose }: { q: string; wsName: string; onClose: () => void }) {
  const nav = useNavigate();
  const openStudy = useOpenStudy();
  const q = useDebounced(typed, 250);
  const { data } = useSearch(q);
  if (!data) return null;
  const secs = sections(data, routeBase(), (to) => { onClose(); nav(to); }, (studyId, series) => { onClose(); openStudy(studyId, series); });
  return (
    <div className="tr-search-menu" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 38, left: 0, width: 520, maxWidth: 'calc(100vw - 32px)', maxHeight: 'min(70vh, 560px)', overflow: 'auto', padding: 6, background: '#fff', border: '1px solid #e3e3e1', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,.12)', zIndex: 30 }}>
      {secs.length === 0 ? <div style={{ padding: '22px 12px', fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>No matches for “{q}” in {wsName}</div> : null}
      {secs.map((sec) => (
        <div key={sec.label} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 10px 4px', fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: '#8a8a8f' }}>
            {sec.label}
            <span style={{ fontWeight: 500, letterSpacing: 0, color: '#b0b0b4' }}>{sec.count}</span>
          </div>
          {sec.items.map((r) => (
            <div key={r.key} data-hit="1" className="tr-hover-track" onClick={r.open} style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr) auto', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>
              {r.num ? (
                <div style={{ width: 40, height: 30, borderRadius: 5, border: '1px solid #ebebea', background: r.cover, display: 'grid', placeItems: 'center', color: '#6b6b70' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{r.num}</span>
                </div>
              ) : (
                <Cover src={r.cover} fallback="#e3e3e1" radius={5} style={{ width: 40, height: 30, border: '1px solid #ebebea' }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500, fontFamily: r.mono ? "'JetBrains Mono', monospace" : 'inherit' }}>{r.title}</span>
                <span className="tr-ellipsis" style={{ fontSize: 12, color: '#8a8a8f' }}>{r.path}</span>
              </div>
              <span style={{ fontSize: 12, color: '#8a8a8f', whiteSpace: 'nowrap' }}>{r.meta}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function TopBar({ guest, search, onSearch }: { guest: boolean | undefined; search: string; onSearch: (q: string) => void }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { data: me } = useSession();
  const signedIn = guest === false;
  const { workspaces, current } = useSelectedWorkspace(signedIn);
  /**
   * Inviting takes administering the workspace you are in: POST
   * /workspaces/:id/members demands workspace_admin, and the workspace page's
   * own invite box is disabled for anybody else and says so.
   *
   * So the button that LEADS there is hidden on the same condition. It used to
   * be offered to everyone, which sent an editor or a viewer to a page whose
   * one purpose was a field they could not type in — with `?invite=1` asking
   * to focus it.
   */
  const canInvite = current?.role === 'workspace_admin';
  const setSelected = useWorkspaceStore((s) => s.setSelected);
  const [wsOpen, setWsOpen] = useState(false);
  const [meOpen, setMeOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const firstHit = useRef<HTMLDivElement>(null);
  const firstHitMenu = useRef<HTMLDivElement>(null);
  useDismiss(wsOpen, () => setWsOpen(false));
  useDismiss(meOpen, () => setMeOpen(false));
  useDismiss(notifOpen, () => setNotifOpen(false));
  useDismiss(searchOpen, () => setSearchOpen(false));
  const [navOpen, setNavOpen] = useState(false);
  useDismiss(navOpen, () => setNavOpen(false));
  // A choice in the menu (or a search result) navigates; the menu goes with it.
  useEffect(() => setNavOpen(false), [loc.pathname]);
  const notifs = useNotifications(signedIn);
  const markRead = useMarkRead();
  const unread = notifs.data?.unread ?? 0;

  const base = routeBase();
  const path = loc.pathname.slice(base.length);
  const onCollections = path.startsWith('/collections');
  const onExplore = path.startsWith('/explore');
  const name = me?.displayName || me?.email || '';

  // A guest's search is Explore's filter: typing lands on Explore with ?q=.
  const guestSearch = (q: string) => {
    onSearch(q);
    const p = new URLSearchParams(loc.search);
    if (q) p.set('q', q);
    else p.delete('q');
    nav({ pathname: `${base}/explore`, search: p.toString() ? `?${p}` : '' }, { replace: onExplore });
  };

  // The workspace list: in the switcher on a wide screen, in the phone menu.
  const workspaceItems = (close: () => void) => (
                <>
                  <MenuLabel>WORKSPACES</MenuLabel>
                  {workspaces.map((w) => (
                    <div
                      key={w.public_id}
                      className="tr-hover-track"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(w.public_id);
                        close();
                        nav(`${base}/`);
                      }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 5, fontSize: 13, cursor: 'pointer', background: w.public_id === current?.public_id ? '#f2f2f0' : 'transparent' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span className="tr-ellipsis" style={{ fontWeight: 500 }}>{w.name}</span>
                        {w.is_default ? <Badge>DEFAULT</Badge> : null}
                      </div>
                      <span style={{ fontSize: 12, color: '#8a8a8f', whiteSpace: 'nowrap' }}>{w.members} members</span>
                    </div>
                  ))}
                  <MenuDivider />
                  <MenuItem icon={<IconSettings size={14} />} onClick={() => { close(); nav(`${base}/workspace`); }}>Manage workspace</MenuItem>
                  <MenuItem icon={<IconWand size={14} />} onClick={() => { close(); nav(`${base}/actions`); }}>Manage actions</MenuItem>
                  <MenuItem icon={<IconPlus size={14} />} onClick={() => { close(); nav(`${base}/workspace?new=1`); }}>New workspace</MenuItem>
                </>
  );

  // The notifications: under the bell, or in the menu once the bell has gone there.
  const notificationList = (close: () => void) => (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px 6px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: '#8a8a8f' }}>NOTIFICATIONS</span>
                    {unread > 0 ? (
                      <a href="#" className="tr-hover-ink-underline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); markRead.mutate({ all: true }); }} style={{ fontSize: 12, color: '#6b6b70', textDecoration: 'none' }}>
                        Mark all as read
                      </a>
                    ) : null}
                  </div>
                  {(notifs.data?.notifications.length ?? 0) === 0 ? <div style={{ padding: '22px 12px', fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>You're all caught up.</div> : null}
                  {(notifs.data?.notifications ?? []).map((n) => (
                    <div
                      key={n.id}
                      className="tr-hover-track"
                      onClick={(e) => {
                        e.stopPropagation();
                        close();
                        if (!n.read) markRead.mutate({ ids: [n.id] });
                        if (n.targetKind === 'study') nav(`${base}/studies/${encodeURIComponent(n.targetId)}`);
                        else nav(`${base}/collections/${n.targetId}#discussion`);
                      }}
                      style={{ display: 'grid', gridTemplateColumns: '28px minmax(0,1fr) 8px', gap: 10, alignItems: 'start', padding: '9px 10px', borderRadius: 6, cursor: 'pointer', background: n.read ? 'transparent' : '#fafaf9' }}
                    >
                      <Avatar name={n.actor} size={28} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 13, lineHeight: 1.4, color: '#0f0f10' }}>
                          {n.kind === 'copy_failed'
                            ? <>A copy into <span style={{ fontWeight: 500 }}>{n.title}</span> did not finish</>
                            : <><strong style={{ fontWeight: 600 }}>{n.actor}</strong> {n.kind === 'reply' ? 'replied to your comment on' : 'commented on'} <span style={{ fontWeight: 500 }}>{n.title}</span></>}
                        </span>
                        <span className="tr-ellipsis" style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{n.preview.length > 90 ? `${n.preview.slice(0, 88)}…` : n.preview}</span>
                        <span style={{ fontSize: 12, color: '#8a8a8f' }}>{relativeTime(n.createdUtc)}</span>
                      </div>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.read ? 'transparent' : '#0f0f10', marginTop: 6 }} />
                    </div>
                  ))}
                </>
  );

  const searchBox = (ref: RefObject<HTMLDivElement>, className: string | undefined, style: CSSProperties) => (
        <div ref={ref} className={className} style={{ position: 'relative', ...style }} onClick={(e) => e.stopPropagation()}>
          <SearchField
            value={search}
            onChange={(q) => {
              if (guest) guestSearch(q);
              else {
                onSearch(q);
                setSearchOpen(true);
              }
            }}
            placeholder="Search"
            h={32}
            bg="#fafaf9"
            fontSize={13}
            radius={7}
            onClear={() => (guest ? guestSearch('') : (onSearch(''), setSearchOpen(false)))}
            onFocus={guest ? (onExplore ? undefined : () => nav(`${base}/explore`)) : () => setSearchOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchOpen(false);
              if (e.key === 'Enter' && !guest) {
                const bar = ref.current?.closest('[data-topbar]');
                const first = [...(bar?.querySelectorAll<HTMLElement>('[data-hit]') ?? [])].find((el) => el.offsetParent !== null);
                first?.click();
              }
            }}
          />
          <span className="tr-search-wide">{signedIn && searchOpen && search.trim() ? <SearchMenu q={search.trim()} wsName={current?.name ?? ''} onClose={() => setSearchOpen(false)} /> : null}</span>
        </div>
  );

  return (
    <div className="tr-topbar" data-topbar="" data-guest={guest ? '' : undefined} style={{ position: 'sticky', top: 0, zIndex: 20, background: '#fff', borderBottom: '1px solid #ebebea' }}>
    <div className="tr-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, height: 55, padding: '0 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: '1 1 auto', minWidth: 0 }}>
        {signedIn || guest ? (
          <span className="tr-bar-menu" style={{ alignItems: 'center', marginRight: -8 }}>
            <MenuAnchor>
              <Button
                variant="ghostIcon"
                size={32}
                title="Menu"
                onClick={(e) => {
                  e.stopPropagation();
                  setNavOpen(!navOpen);
                  setMeOpen(false);
                  setNotifOpen(false);
                  setWsOpen(false);
                }}
              >
                <IconList size={18} />
              </Button>
              {navOpen ? (
                <MenuSurface top={38} left={0} width={280}>
                  <div className="tr-menu-search" style={{ padding: '2px 2px 6px' }}>
                    {searchBox(firstHitMenu, undefined, { width: '100%' })}
                  </div>
                  <div className="tr-menu-nav">
                    {!guest ? <MenuItem onClick={() => { setNavOpen(false); nav(`${base}/collections`); }}>Collections</MenuItem> : null}
                    <MenuItem onClick={() => { setNavOpen(false); nav(`${base}/explore`); }}>Explore</MenuItem>
                    {signedIn ? <MenuDivider /> : null}
                  </div>
                  {signedIn ? (
                    <div className="tr-menu-ws">
                      {workspaceItems(() => setNavOpen(false))}
                      {canInvite ? <MenuItem icon={<IconUserPlus size={14} />} onClick={() => { setNavOpen(false); nav(`${base}/workspace?invite=1`); }}>Invite people</MenuItem> : null}
                    </div>
                  ) : null}
                  {signedIn ? (
                    <div className="tr-menu-bell">
                      <MenuDivider />
                      {notificationList(() => setNavOpen(false))}
                    </div>
                  ) : null}
                </MenuSurface>
              ) : null}
            </MenuAnchor>
          </span>
        ) : null}
        {/* A guest's logo goes to the website where one owns `/`: a document
            load, since `/` is then the website's page and not a route here. */}
        <Link to={guest && !getConfig().website ? `${base}/explore` : `${base}/`} reloadDocument={guest && getConfig().website} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', height: 55, flex: 'none' }}>
          <img className="tr-logo-full" src={`${BASE}/brand/logo-horizontal.svg`} alt="TileRiver" style={{ height: 22, display: 'block', marginTop: -5 }} />
          <img className="tr-logo-icon" src={`${BASE}/brand/logo-icon.svg`} alt="TileRiver" style={{ height: 24, width: 27 }} />
        </Link>
        <div className="tr-bar-nav" style={{ width: 1, height: 20, background: '#e3e3e1' }} />
        <div className="tr-bar-nav" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {!guest ? <NavLink to={`${base}/collections`} label="Collections" active={onCollections} /> : null}
          <NavLink to={`${base}/explore`} label="Explore" active={onExplore} />
        </div>
        {searchBox(firstHit, 'tr-bar-search', { flex: '1 1 200px', maxWidth: 320, minWidth: 140 })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
        {signedIn ? (
          <>
            <UploadPill />
            <span className="tr-bar-ws" style={{ display: 'flex' }}>
            <MenuAnchor>
              <div
                className="tr-hover-soft"
                onClick={(e) => {
                  e.stopPropagation();
                  setWsOpen(!wsOpen);
                  setMeOpen(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, minWidth: 140, maxWidth: 220, padding: '0 8px 0 10px', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <span className="tr-ellipsis" style={{ fontSize: 13.5, fontWeight: 500, flex: 1, minWidth: 0 }}>{current?.name ?? ''}</span>
                <IconChevronsUpDown size={12} color="#6b6b70" />
              </div>
              {wsOpen ? (
                <MenuSurface top={38} right={0} width={300}>
                  {workspaceItems(() => setWsOpen(false))}
                </MenuSurface>
              ) : null}
            </MenuAnchor>
            </span>
            {canInvite ? (
              <span className="tr-bar-ws" style={{ display: 'flex' }}>
                <Button variant="outlined" size={32} icon={<IconUserPlus size={14} />} onClick={() => nav(`${base}/workspace?invite=1`)}>
                  Invite
                </Button>
              </span>
            ) : null}
            <div className="tr-bar-ws" style={{ width: 1, height: 20, background: '#e3e3e1', margin: '0 4px' }} />
            <span className="tr-bar-bell" style={{ display: 'flex' }}>
            <MenuAnchor>
              <Button
                variant="ghostIcon"
                size={32}
                title="Notifications"
                style={{ position: 'relative' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setNotifOpen(!notifOpen);
                  setMeOpen(false);
                  setWsOpen(false);
                }}
              >
                <IconBell size={16} />
                {unread > 0 ? (
                  <span style={{ position: 'absolute', top: 1, right: 1, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 9, background: '#0f0f10', color: '#fff', fontSize: 10, fontWeight: 600, display: 'grid', placeItems: 'center', border: '2px solid #fff', lineHeight: 1 }}>
                    {unread > 9 ? '9+' : unread}
                  </span>
                ) : null}
              </Button>
              {notifOpen ? (
                <MenuSurface top={38} right={0} width={360} maxHeight="min(70vh, 520px)">
                  {notificationList(() => setNotifOpen(false))}
                </MenuSurface>
              ) : null}
            </MenuAnchor>
            </span>
            <MenuAnchor>
              <Avatar
                name={name}
                size={30}
                ring={meOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setMeOpen(!meOpen);
                  setWsOpen(false);
                }}
              />
              {meOpen ? (
                <MenuSurface top={38} right={0} width={260}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 10px' }}>
                    <Avatar name={name} size={34} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                      <span className="tr-ellipsis" style={{ fontSize: 13.5, fontWeight: 500 }}>{name}</span>
                      <span className="tr-ellipsis" style={{ fontSize: 12, color: '#8a8a8f' }}>{me?.email}</span>
                    </div>
                  </div>
                  <MenuDivider />
                  <MenuItem icon={<IconUser size={14} color="#6b6b70" />} onClick={() => { setMeOpen(false); nav(`${base}/account/profile`); }}>Profile</MenuItem>
                  <MenuItem icon={<IconDatabase size={14} color="#6b6b70" />} onClick={() => { setMeOpen(false); nav(`${base}/account/usage`); }}>Usage</MenuItem>
                  <MenuItem icon={<IconSettings size={14} color="#6b6b70" />} onClick={() => { setMeOpen(false); nav(`${base}/account/settings`); }}>Settings</MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={<IconLogout size={14} color="#6b6b70" />}
                    onClick={async () => {
                      setMeOpen(false);
                      // It loads `/`: the website where one owns it, Explore
                      // otherwise (RootRoute).
                      await signOut();
                    }}
                  >
                    Sign out
                  </MenuItem>
                </MenuSurface>
              ) : null}
            </MenuAnchor>
          </>
        ) : guest ? (
          <>
            <a href="#" className="tr-hover-underline" onClick={(e) => { e.preventDefault(); nav(`${base}/signin`); }} style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: 13, fontWeight: 500, color: '#0f0f10', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Sign in
            </a>
            <a href="#" className="tr-hover-ink2" onClick={(e) => { e.preventDefault(); nav(`${base}/signup`); }} style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 14px', border: 0, borderRadius: 7, background: '#0f0f10', fontSize: 13, fontWeight: 500, color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <span className="tr-signup-long">Create free account</span>
              <span className="tr-signup-short">Sign up</span>
            </a>
          </>
        ) : null}
      </div>
    </div>
    {/* Under 1024px results open full width under the bar, whichever box was typed in. */}
    <div className="tr-search-narrow">{signedIn && searchOpen && search.trim() ? <SearchMenu q={search.trim()} wsName={current?.name ?? ''} onClose={() => setSearchOpen(false)} /> : null}</div>
    </div>
  );
}
